import PhoneNumberField from '@/components/Forms/Fields/PhoneNumberField.tsx';
import GreyButton from '@/components/Forms/GreyButton.tsx';
import {zodResolver} from '@hookform/resolvers/zod';
import LoginIcon from '@mui/icons-material/Login';
import {Alert, Box, Button, Divider, MenuItem, Stack} from '@mui/material';
import {RhfTextField} from 'mui-rhf-integration';
import type * as React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {z} from 'zod';
import {isState, phoneValidation, postalCodeUS, states} from '../Helpers/validationHelpers';

export type CreateEnrollmentValues = {
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    address: string;
    city: string;
    state: string;
    zip: string;
};

type Props = {
    handleCreateEnrollmentFormSubmit: (values: CreateEnrollmentValues) => void;
    handleBack: () => void;
    hasMessage: boolean;
    hasError: boolean;
    message: string;
};

const CreateEnrollmentForm = ({
    handleCreateEnrollmentFormSubmit,
    handleBack,
    hasMessage,
    hasError,
    message,
}: Props): React.ReactElement => {
    const {t} = useTranslation();

    const initialValues = {
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
    };

    const schema = z.object({
        email: z.string().email({message: `${t('sefEmail')} ${t('requiredError')}`}),
        firstName: z.string({message: `${t('sefFirstName')} ${t('requiredError')}`}),
        lastName: z.string({message: `${t('sefLastName')} ${t('requiredError')}`}),
        phone: phoneValidation(`${t('sefPhone')} ${t('requiredError')}`),
        address: z.string({message: `${t('sefAddress')} ${t('requiredError')}`}),
        city: z.string({message: `${t('sefCity')} ${t('requiredError')}`}),
        state: isState,
        zip: postalCodeUS(`${t('sefZipCode')} ${t('requiredError')}`),
    });

    type CreateEnrollmentSchemaType = z.infer<typeof schema>;

    const form = useForm<CreateEnrollmentSchemaType>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
    });

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(handleCreateEnrollmentFormSubmit)}>
                <Stack sx={{p: 3, pb: 1, mt: 2}} alignItems={'center'}>
                    <Box sx={{textAlign: 'center'}}>
                        {t('beforeCreate1')} <b>{t('beforeCreate2')}</b> {t('button')}.
                    </Box>
                    <Box sx={{mt: 3, textAlign: 'center'}}>{t('beforeNote')}</Box>
                    <Stack
                        spacing={5}
                        sx={{width: '100%', maxWidth: '600px', mt: 3, alignItems: 'center'}}
                    >
                        <RhfTextField
                            name={'email'}
                            label={`${t('sefEmail')}:`}
                            control={form.control}
                            fullWidth
                        />
                        <RhfTextField
                            name={'firstName'}
                            label={`${t('sefFirstName')}:`}
                            control={form.control}
                            fullWidth
                        />
                        <RhfTextField
                            name={'lastName'}
                            label={`${t('sefLastName')}:`}
                            control={form.control}
                            fullWidth
                        />
                        <PhoneNumberField label={`${t('sefPhone')}:`} name={'phone'} />
                        <RhfTextField
                            name={'address'}
                            label={`${t('sefAddress')}:`}
                            control={form.control}
                            fullWidth
                        />
                        <Stack
                            direction={{xs: 'column', md: 'row'}}
                            spacing={2}
                            sx={{width: '100%', maxWidth: '600px'}}
                        >
                            <RhfTextField
                                name={'city'}
                                label={`${t('sefCity')}:`}
                                control={form.control}
                                fullWidth
                            />
                            <RhfTextField
                                control={form.control}
                                name={'state'}
                                label={t('sefState')}
                                select
                                fullWidth
                            >
                                {states.map(state => (
                                    <MenuItem value={state}>{state}</MenuItem>
                                ))}
                            </RhfTextField>
                            <RhfTextField
                                name={'zip'}
                                label={`${t('sefZipCode')}:`}
                                control={form.control}
                                fullWidth
                            />
                        </Stack>
                        <Divider sx={{py: 2, width: '100%', borderColor: '#000'}} />
                    </Stack>
                </Stack>
                {hasError && (
                    <Alert
                        key={'global-error'}
                        icon={false}
                        variant={'standard'}
                        color={'error'}
                        sx={{mx: 3, mb: 1}}
                    >
                        {message}
                    </Alert>
                )}
                {hasMessage && (
                    <Alert
                        key={'global-error'}
                        icon={false}
                        variant={'standard'}
                        color={'warning'}
                        sx={{mx: 3, mb: 1}}
                    >
                        {message}
                    </Alert>
                )}
                <Stack
                    sx={{
                        px: 3,
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        mb: 3,
                    }}
                >
                    <GreyButton callback={handleBack} />
                    <Button
                        type={'submit'}
                        color={!form.formState.isValid ? 'error' : 'secondary'}
                        variant={!form.formState.isValid ? 'contained' : 'outlined'}
                        disabled={form.formState.isLoading || !form.formState.isValid}
                    >
                        {t('login')} &nbsp;
                        <LoginIcon />
                    </Button>
                </Stack>
            </form>
        </FormProvider>
    );
};

export default CreateEnrollmentForm;
